/**
 * Collection grid: Load More button + Infinite scroll.
 *
 * Renders as <load-more-products data-mode="load_more|infinite" data-next-url="...">.
 * Lives inside #main-collection-product-grid, so it is re-created (and this element
 * re-initialises via connectedCallback) every time the facets component replaces the
 * grid after a filter/sort change. data-next-url carries the active filters because
 * it is built from Shopify's paginate.next.url.
 */
if (!customElements.get('load-more-products')) {
  class LoadMoreProducts extends HTMLElement {
    connectedCallback() {
      this.grid = document.getElementById('main-collection-product-grid');
      this.button = this.querySelector('[data-load-more-button]');
      this.mode = this.dataset.mode;
      this.loading = false;

      if (this.button) {
        this._onClick = () => this.loadNext();
        this.button.addEventListener('click', this._onClick);
      }

      if (this.mode === 'infinite') {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.loadNext();
            });
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
    }

    async loadNext() {
      const url = this.dataset.nextUrl;
      if (this.loading || !url || !this.grid) return;

      this.loading = true;
      this.setAttribute('data-loading', '');
      if (this.button) this.button.disabled = true;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        const nextGrid = doc.getElementById('main-collection-product-grid');
        if (nextGrid) {
          nextGrid
            .querySelectorAll(':scope > [data-js-product-item]')
            .forEach((card) => this.grid.insertBefore(card, this));
        }

        // Carry on to the page after this one, or stop when there are no more.
        const nextTrigger = doc.querySelector('load-more-products');
        const nextUrl = nextTrigger ? nextTrigger.getAttribute('data-next-url') : '';

        if (nextUrl) {
          this.dataset.nextUrl = nextUrl;
        } else {
          if (this.observer) this.observer.disconnect();
          this.remove();
        }

        // Let the section re-apply collection-scoped card links to the new cards.
        document.dispatchEvent(
          new CustomEvent('krown:main-collection-grid:filtered', {
            bubbles: true,
            detail: { gridElement: document.getElementById('CollectionProductGrid') },
          })
        );

        // Ensure any lazy-loaded card scripts are registered for the section.
        const sectionId = this.grid.dataset.id;
        if (window.KROWN && typeof window.KROWN.loadSectionAssets === 'function') {
          window.KROWN.loadSectionAssets(sectionId);
        }
      } catch (error) {
        console.error('[load-more-products]', error);
      } finally {
        this.loading = false;
        this.removeAttribute('data-loading');
        if (this.button) this.button.disabled = false;
      }
    }
  }

  customElements.define('load-more-products', LoadMoreProducts);
}